import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Divider, FilterChip, Table, ToggleButtonGroup, VisuallyHidden } from '@zillow/constellation-10';
import { PageContent } from '@zillow/constellation';

import { AppSelectors, PropertiesSelectors, UserSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';
import useVisibility from '../../../hooks/useVisibility';

import PropertiesActions from '../../../actions/PropertiesActions';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';

import { getCurrentMonthString } from '../../../utils/dateUtils';

import { ControlledSearchBox } from '../../../components/ControlledSearchBox';

import { flexContainerStyles, rightSubFlexContainerStyles, stickySubHeadingSectionContainerRecipe } from '../styles';
import { TableHeaderCell } from '../../../components/properties/PropertyRow/styles';

const { PROPERTIES_SUBNAV_FILTER_OPTIONS, PROPERTIES_SUBNAV_FILTER_TYPE } = PropertiesConstants;
const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const baseHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
];

const groupedHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET,
];

const subNavFilterOptions = {
  options: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS.reduce(
    (acc, subNav) => {
      acc[subNav.id] = { ...subNav };

      return acc;
    },
    {} as Record<string, PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE>,
  ),
};

type PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE = (typeof PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS)[0];

type Update = {
  includeActive: boolean;
  includeInactive: boolean;
  includeArchived: boolean;
  includeUnarchived: boolean;
  subNavFilterType: string;
  startKeyExclusive: string;
  includeAtRisk: boolean;
  includeFreeBonusListingsOnly: boolean;
  includeFreeClaimedListingsOnly: boolean;
};

type Props = {
  searchText: string;
  listingsLoading: boolean;
  onSearchFilterUpdate: (arg: string) => void;
  createQueryObjectAndRedirectTo: (searchText: string, subNavFilterType: string) => void;
  updateFiltersAndListings: ({ update }: { update: Update }) => void;
  numDisplayedListings: number;
};

const SubHeadingSection = ({
  searchText,
  listingsLoading,
  onSearchFilterUpdate,
  createQueryObjectAndRedirectTo,
  updateFiltersAndListings,
  numDisplayedListings,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  // Local state for immediate UI feedback
  const [searchInput, setSearchInput] = useState(searchText);

  // Sync searchInput with searchText when it changes from external sources
  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  const {
    listingCount: {
      active: numActiveListings,
      atRisk: numAtRiskListings,
      inactive: numInactiveListings,
      archived: numArchivedListings,
      freeBonusListingCount: numBonusListings,
      freeClaimedListingCount: numClaimedListings,
      total: numTotalListings,
    },
  } = useSelector((state) => PropertiesSelectors.makeProperties(state));
  const { subNavFilterType } = useSelector((state) => PropertiesSelectors.makePropertiesFilters(state));
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));

  const [isVisible, isVisibleRef] = useVisibility(0, 100, 'above');

  const canViewSearch = numTotalListings >= 5;

  const subNavFiltersOnSelectHandler = ({
    includeActive,
    includeInactive,
    id: subNavFilterType,
    includeAtRisk,
    includeFreeBonusListingsOnly,
    includeFreeClaimedListingsOnly,
    includeArchived,
    includeUnarchived,
  }: {
    includeActive: boolean;
    includeInactive: boolean;
    id: string;
    includeAtRisk: boolean;
    includeFreeBonusListingsOnly: boolean;
    includeFreeClaimedListingsOnly: boolean;
    includeArchived: boolean;
    includeUnarchived: boolean;
  }) => {
    dispatch(PropertiesActions.updateFiltersTouched(true));

    createQueryObjectAndRedirectTo(searchText, subNavFilterType);

    updateFiltersAndListings({
      update: {
        includeActive,
        includeInactive,
        includeArchived,
        includeUnarchived,
        subNavFilterType,
        startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
        includeAtRisk,
        includeFreeBonusListingsOnly,
        includeFreeClaimedListingsOnly,
      },
    });

    PropertiesPageCSEvents.PropertyListFilter({
      user,
      triggerObject: PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS[subNavFilterType],
    });
  };

  const countMap = {
    [PROPERTIES_SUBNAV_FILTER_TYPE.ALL]: numTotalListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET]: numActiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET]: numInactiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK]: numAtRiskListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED]: numClaimedListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS]: numBonusListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED]: numArchivedListings,
  };
  PROPERTIES_SUBNAV_FILTER_OPTIONS.forEach((subNav) => {
    subNavFilterOptions.options[subNav.id].count = countMap[subNav.id] || 0;
  });

  const placeholderText = 'Search by city, address and ZIP code';

  const handleSubNavFilterChipClick = (id: string) => {
    // treat archived filter as a toggle between all and archived view when there are no unarchived listings
    if (id === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED && numTotalListings === 0) {
      subNavFiltersOnSelectHandler(subNavFilterOptions.options[PROPERTIES_SUBNAV_FILTER_TYPE.ALL]);
    } else {
      subNavFiltersOnSelectHandler(subNavFilterOptions.options[id]);
    }
  };

  const renderSubNavFilterChip = (id: string, displayLabel: string) => {
    return (
      <FilterChip
        key={id}
        id={id}
        value={id}
        selected={id === subNavFilterType}
        disabled={listingsLoading}
        onClick={() => handleSubNavFilterChipClick(id)}
        data-eventid={PropertiesPageCSEvents.PropertyListFilter.data_event_id}
        css={{ margin: '0 {spacing.200} !important', borderRadius: 'Chip !important' }}
      >
        {displayLabel}
      </FilterChip>
    );
  };

  const renderSubNavButton = ({ id, label, count }: PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE) => {
    const displayLabel = `${label} (${count})`;

    const isBaseHideableTab = baseHideableTabs.includes(id) && count === 0 && id !== subNavFilterType;
    const isGroupedHideableTab = groupedHideableTabs.includes(id) && count === 0 && numTotalListings === 0;

    if (isBaseHideableTab || isGroupedHideableTab) {
      return <Fragment key={id} />;
    }

    return isScreenLgGTE && id === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED
      ? [
          numTotalListings > 0 && <Divider orientation="vertical" css={{ height: '700', marginInline: '200' }} />,
          renderSubNavFilterChip(id, displayLabel),
        ]
      : renderSubNavFilterChip(id, displayLabel);
  };

  const renderSearchAndFilters = () => (
    <PageContent
      data-testid="SubHeadingSection"
      padding="xs"
      marginTop={{ default: 'md', lg: '0' }}
      marginBottom={isScreenLgGTE ? '0' : 'md'}
    >
      {
        <Box data-testid="SubHeadingSection-Row" css={flexContainerStyles}>
          {canViewSearch && (
            <Box css={rightSubFlexContainerStyles}>
              <Box css={{ width: '100%', maxWidth: '370px' }}>
                <ControlledSearchBox
                  placeholder={placeholderText}
                  value={searchInput}
                  onChange={setSearchInput}
                  onSearch={onSearchFilterUpdate}
                  isSearching={listingsLoading && Boolean(searchText)}
                  disabled={listingsLoading}
                />
              </Box>
            </Box>
          )}
          <ToggleButtonGroup
            aria-label="SubNav Filter Tabs"
            css={{
              flexWrap: { base: 'wrap', lg: 'nowrap' },
              gap: '{spacing.200} 0',
              maxWidth: { base: '100%', lg: canViewSearch ? '50%' : '100%' },
              marginRight: { lg: '400' },
              ...(canViewSearch && { lgDown: { marginTop: '400' } }),
            }}
          >
            <>
              {Object.keys(subNavFilterOptions.options).map((id) =>
                renderSubNavButton(subNavFilterOptions.options[id] as PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE),
              )}
            </>
          </ToggleButtonGroup>
        </Box>
      }
    </PageContent>
  );

  const isStaticPosition = !!isVisible || numDisplayedListings <= 5;

  return (
    <>
      <div ref={isVisibleRef as React.RefObject<HTMLDivElement>} />
      <Box css={stickySubHeadingSectionContainerRecipe.raw({ isVisible: isStaticPosition || listingsLoading })}>
        {renderSearchAndFilters()}
        {!listingsLoading && isScreenLgGTE && numDisplayedListings > 0 && (
          <Table.Header
            css={{
              display: 'block',
              borderTop: '1px solid {colors.border.subtle}',
              backgroundColor: 'bg.softer',
              padding: isStaticPosition ? '0' : '0 {spacing.600}',
            }}
          >
            <Table.Row css={{ display: 'flex', maxWidth: '1232px', margin: 'auto' }}>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.ADDRESS}>Address</TableHeaderCell>
              {subNavFilterType !== PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED && (
                <>
                  <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.RENT}>
                    {getCurrentMonthString(new Date())} rent
                  </TableHeaderCell>
                  <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS}>Potential tenants</TableHeaderCell>
                  <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.LEASES}>Lease</TableHeaderCell>
                  <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.ACTIONS}>
                    <VisuallyHidden>Actions</VisuallyHidden>
                  </TableHeaderCell>
                </>
              )}
            </Table.Row>
          </Table.Header>
        )}
      </Box>
    </>
  );
};

export default SubHeadingSection;
